/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import {
  IShippingAddress,
  ISearchShippingAddressReq,
} from '@/interfaces/shipping-address';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { importShippingAddress } from '@/adapters/shipping-address';
import { twMerge } from 'tailwind-merge';
import { isEmpty, isUndefined } from 'lodash';
import {
  getGeography,
  getGeographyBySubdistrictCode,
} from '@/utils/geographyUtils';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchShippingAddressReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ImportShippingAddressModal = ({
  open,
  handleOk,
  handleCancel,
}: IProps) => {
  const [criteria, setCriteria] = useState<ISearchShippingAddressReq>({
    ..._criteria,
  });
  const [shippingAddress, setShippingAddress] = useState<IShippingAddress[]>(
    [],
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IShippingAddress> | SorterResult<IShippingAddress>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IShippingAddress) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<IShippingAddress>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'รหัส',
      dataIndex: 'customerCode',
      key: 'customerCode',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'สถานที่',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      className: 'font-bold',
    },
    {
      title: 'เบอร์ติดต่อ',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      className: 'font-medium',
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'ถนน',
      dataIndex: 'road',
      key: 'road',
    },
    {
      title: 'ตำบล',
      dataIndex: 'subdistrictNameEn',
      key: 'subdistrictNameEn',
    },
    {
      title: 'อำเภอ',
      dataIndex: 'districtNameEn',
      key: 'districtNameEn',
    },
    {
      title: 'จังหวัด',
      dataIndex: 'provinceNameEn',
      key: 'provinceNameEn',
    },
    {
      title: 'รหัสไปรษณีย์',
      dataIndex: 'postalCode',
      key: 'postalCode',
    },
    {
      title: 'เบอร์โทรศัพท์',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Google Map',
      dataIndex: 'maplink',
      key: 'maplink',
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      sorter: (a, b) => Number(a.status) - Number(b.status),
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() =>
            setShippingAddress(c =>
              c.filter(x => x.customerCode !== rec.customerCode),
            )
          }
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setShippingAddress([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
        .map((c: any) => {
          const subDistrictData = getGeographyBySubdistrictCode(Number(c[4]));
          const geo = getGeography(Number(c[10]));
          const customer: IShippingAddress = {
            customerCode: c[0]?.toString() || '',
            name: c[1]?.toString() || '',
            address: c[2]?.toString() || '',
            road: c[3]?.toString() || '',
            subdistrictNameEn: subDistrictData?.subdistrictNameEn || '',
            subdistrictNameTh: subDistrictData?.subdistrictNameEn || '',
            districtNameEn: geo?.districtNameEn,
            districtNameTh: geo?.districtNameEn,
            provinceNameEn: geo?.provinceNameEn,
            provinceNameTh: geo?.provinceNameEn,
            // districtNameEn: c[6]?.toString() || '',
            // districtNameTh: c[7]?.toString() || '',
            // provinceNameEn: c[8]?.toString() || '',
            // provinceNameTh: c[9]?.toString() || '',
            postalCode: Number(c[10]),
            phoneNumber: c[11]?.toString() || '',
            maplink: c[12]?.toString() || '',
            remark: c[13]?.toString() || '',
            status: 1,
          } as IShippingAddress;
          return customer;
        });
      setShippingAddress(excelData);
      return false;
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutations = useMutation({
    mutationFn: async () => await importShippingAddress(shippingAddress),
    mutationKey: ['import-shippingAddress'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setShippingAddress([]);
        notification.success({
          message: 'นำเข้าที่อยู่ลูกค้าสำเร็จ',
        });
        handleOk();
      } else {
        const duplicateShippingAddresss: string[] = data.data?.data;
        const updateShippingAddress = shippingAddress.map(c => {
          c.status = duplicateShippingAddresss.includes(c.customerCode ?? '')
            ? 2
            : 3;
          return c;
        });
        setShippingAddress(updateShippingAddress);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    mutations.mutateAsync();
  };

  return (
    <Modal
      title="นำเข้าที่อยู่จัดส่ง"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<IShippingAddress>
          columns={columns}
          criteria={criteria}
          data={{
            data: shippingAddress,
            totalRecords: shippingAddress.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          scroll
          scrollLength={2000}
        />
      </div>
    </Modal>
  );
};

export default ImportShippingAddressModal;
